import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Search, ArrowUp, ArrowDown, UserCheck, ChevronLeft, ChevronRight, CalendarDays, Download } from "lucide-react";
import { toast } from "sonner";

const ITEMS_PER_PAGE = 20;
const HISTORY_COL_STORAGE_KEY = "arrivo_history_col_order";

function getTodayDateKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getYesterdayKey() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return getDateKey(d);
}

function get30DaysAgoKey() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return getDateKey(d);
}

function formatDisplayClass(cls) {
  if (!cls) return "—";
  if (cls.toUpperCase() === "SUV") return "SUV";
  return cls.charAt(0).toUpperCase() + cls.slice(1);
}

// House net = what the operator keeps, after paying the driver and remitting
// government pass-throughs (NY tax, PA Fee, Congestion Fee, BCF).
// Cancelled / no-show jobs return null so the column shows "—".
function computeHouseNet(job) {
  const status = job.status;
  if (status === "cancelled") return null;
  const total = parseFloat(job.total) || 0;
  const driverTotal = parseFloat(job.driver_total) || 0;
  const tax = parseFloat(job.tax) || 0;
  const paFee = parseFloat(job.pa_fee) || 0;
  const cgFee = parseFloat(job.cg_fee) || 0;
  const mode = job.pricing_mode || "all_inclusive";
  const bcf = parseFloat(mode === "line_items" ? job.bcf_charge : job.bcf_liability) || 0;
  const sqFee = parseFloat(job.square_processing_fee) || 0;
  if (total === 0 && driverTotal === 0) return null;
  return total - driverTotal - tax - paFee - cgFee - bcf - sqFee;
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
}

function formatChildSeats(job) {
  const rear = job.child_seats_rear || 0;
  const front = job.child_seats_front || 0;
  const booster = job.child_seats_booster || 0;
  if (rear === 0 && front === 0 && booster === 0) return null;
  const parts = [];
  if (front >= 1) parts.push(`${front} Front-facing`);
  if (rear >= 1) parts.push(`${rear} Rear-facing`);
  if (booster >= 1) parts.push(`${booster} Booster`);
  return parts.join(", ");
}

function formatTime(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatDateDisplay(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString();
}

function formatPickupDate(dt) {
  if (!dt) return "—";
  const [y, m, d] = dt.split("T")[0].split("-");
  return `${m}/${d}/${y}`;
}

function formatPickupTime(dt) {
  if (!dt) return "—";
  const t = dt.split("T")[1];
  if (!t) return "—";
  const [h, m] = t.split(":");
  return `${h}:${m}`;
}

// ========== READ-ONLY FIELD ==========
function ReadOnlyField({ label, value, mono }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={`text-sm text-foreground px-2.5 py-1.5 rounded bg-muted/20 border border-border min-h-[32px] flex items-center ${!value ? "text-muted-foreground/50" : ""}`}
        style={mono ? { fontFamily: "'IBM Plex Sans', monospace" } : undefined}
      >
        {value || "—"}
      </div>
    </div>
  );
}

// ========== HOVER PREVIEW ==========
function RowHoverPreview({ job, children }) {
  const isMeetGreet = (job.special_services || []).includes("meet_greet");
  const isChildSeat = (job.special_services || []).includes("child_seat");
  const isPetFriendly = (job.special_services || []).includes("pet_friendly");
  const childSeatsText = formatChildSeats(job);
  const hasServices = isMeetGreet || isChildSeat || isPetFriendly;
  const hasInstructions = job.special_instructions && job.special_instructions.trim().length > 0;
  const hasTotal = (job.total || 0) > 0;
  const hasPax = job.passenger_count > 1;
  const hasLuggage = job.luggage && job.luggage.trim().length > 0;
  const hasAnything = hasServices || hasInstructions || hasTotal || hasPax || hasLuggage;

  if (!hasAnything) return children;

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-auto max-w-[280px] p-3 bg-card border-border shadow-lg"
      >
        <div className="space-y-2" data-testid="hover-preview">
          {hasPax && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Passengers</span>
              <span className="text-xs text-foreground font-medium" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{job.passenger_count}</span>
            </div>
          )}
          {hasLuggage && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Luggage</span>
              <span className="text-xs text-foreground">{job.luggage}</span>
            </div>
          )}
          {hasServices && (
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Services</span>
              <div className="flex flex-wrap gap-1">
                {isMeetGreet && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30 font-medium">M&G</span>}
                {isChildSeat && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-400/15 text-orange-300 border border-orange-400/30 font-medium">{childSeatsText || "Child Seat"}</span>}
                {isPetFriendly && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-400/15 text-orange-300 border border-orange-400/30 font-medium">Pet</span>}
              </div>
            </div>
          )}
          {hasInstructions && (
            <div className="space-y-0.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</span>
              <p className="text-xs text-foreground leading-relaxed line-clamp-3">{job.special_instructions}</p>
            </div>
          )}
          {hasTotal && (
            <div className="flex items-center justify-between gap-4 pt-1 border-t border-border">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</span>
              <span className="text-xs text-foreground font-semibold" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>${job.total.toFixed(2)}</span>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// ========== VIEW-ONLY DIALOG ==========
function JobViewDialog({ job, open, onClose, getDriverName }) {
  if (!job) return null;

  const total = job.total || 0;
  const isMeetGreet = (job.special_services || []).includes("meet_greet");
  const isChildSeat = (job.special_services || []).includes("child_seat");
  const isPetFriendly = (job.special_services || []).includes("pet_friendly");
  const childSeatsText = formatChildSeats(job);
  const driverName = job.driver_id ? getDriverName(job.driver_id) : null;
  const pickupDate = formatPickupDate(job.pickup_datetime);
  const pickupTime = formatPickupTime(job.pickup_datetime);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid="history-job-view-dialog">
        <DialogHeader className="px-6 pt-5 pb-0 pr-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>Job Details</DialogTitle>
              {job.status === "no_show" ? (
                <span className="text-xs font-bold text-red-500 tracking-wider px-1" title="No-show">NS</span>
              ) : (
                <span
                  className={`inline-block w-3.5 h-3.5 rounded-[3px] ${
                    job.status === "cancelled" ? "bg-red-500/80" : "bg-emerald-400/80"
                  }`}
                  title={job.status}
                />
              )}
              <span className="text-xs text-muted-foreground capitalize">{job.status}</span>
            </div>
            <div className="text-center flex items-center justify-center gap-1.5">
              <img src="/logo-mark.png" alt="Arrivo" className="w-4 h-4 object-contain" />
              <span className="text-lg font-light tracking-[0.3em] text-primary" style={{ fontFamily: "'Manrope', sans-serif" }}>ARRIVO</span>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{pickupDate}</div>
              <div className="text-sm font-semibold text-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                {pickupTime}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-[1fr_1fr_240px] gap-0 border-t border-border mt-3">
          {/* COLUMN 1: PASSENGER */}
          <div className="p-5 border-r border-border space-y-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Passenger</div>
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="First Name" value={job.first_name} />
              <ReadOnlyField label="Last Name" value={job.last_name} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Phone" value={job.client_phone} />
              <ReadOnlyField label="Email" value={job.client_email} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <ReadOnlyField label="Account #" value={job.account_number} />
              <ReadOnlyField label="Riders" value={job.passenger_count?.toString()} mono />
              <ReadOnlyField label="Luggage" value={job.luggage} />
            </div>
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Services</div>
              <div className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold ${
                isMeetGreet ? "bg-primary/20 text-primary border-2 border-primary" : "bg-muted/10 text-muted-foreground/40 border border-border"
              }`}>
                <UserCheck className="w-4 h-4" />
                Meet & Greet
              </div>
              <div className="flex gap-1.5">
                <div className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium text-center ${
                  isChildSeat ? "bg-orange-400/15 text-orange-300 border border-orange-400/40" : "bg-muted/10 text-muted-foreground/40 border border-border"
                }`}>Child Seat</div>
                <div className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium text-center ${
                  isPetFriendly ? "bg-orange-400/15 text-orange-300 border border-orange-400/40" : "bg-muted/10 text-muted-foreground/40 border border-border"
                }`}>Pet Friendly</div>
              </div>
              {isChildSeat && childSeatsText && (
                <div className="rounded-md border border-orange-400/30 bg-orange-400/5 p-3 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-orange-400 font-medium">Child Seat Details</div>
                  <div className="text-xs text-foreground">{childSeatsText}</div>
                </div>
              )}
            </div>
          </div>

          {/* COLUMN 2: TRIP DETAILS */}
          <div className="p-5 border-r border-border space-y-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Trip Details</div>
            <ReadOnlyField label="Pickup Location" value={job.pickup_location} />
            <ReadOnlyField label="Dropoff Location" value={job.dropoff_location} />
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Date" value={pickupDate} mono />
              <ReadOnlyField label="Time" value={pickupTime} mono />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Vehicle Class" value={formatDisplayClass(job.vehicle_class || job.vehicle_type)} />
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Driver #</div>
                <div className="text-sm px-2.5 py-1.5 rounded bg-muted/20 border border-border min-h-[32px] flex items-center gap-2" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                  {job.driver_number ? (
                    <span className="text-cyan-700 dark:text-cyan-300 font-semibold">{job.driver_number}</span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                  {driverName && driverName !== "—" && (
                    <span className="text-xs text-muted-foreground font-normal">({driverName})</span>
                  )}
                </div>
              </div>
            </div>
            {job.special_instructions && (
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Special Instructions</div>
                <div className="text-sm text-foreground px-2.5 py-2 rounded bg-muted/20 border border-border whitespace-pre-wrap">{job.special_instructions}</div>
              </div>
            )}
          </div>

          {/* COLUMN 3: PRICING */}
          <div className="p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Pricing</div>
            <div className="rounded-lg bg-muted/20 border border-border p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total</div>
              <div className="text-2xl font-bold text-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>${total.toFixed(2)}</div>
            </div>
            <div className="space-y-2 pt-1">
              {[
                { key: "base_rate", label: "Base Rate" },
                { key: "tolls", label: "Tolls" },
                { key: "gratuity", label: "Gratuity" },
                { key: "tax", label: "Tax" },
                { key: "pa_fee", label: "PA Fee" },
                { key: "cg_fee", label: "CG Fee" },
                { key: "bcf_charge", label: "BCF" },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
                  <span className="text-xs text-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>${(parseFloat(job[key]) || 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-border pt-2 flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">Total</span>
                <span className="text-sm font-bold text-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== HISTORY COLUMN DEFINITIONS ==========
const HISTORY_COLUMNS = [
  { key: "job_number", label: "Job#", w: "w-[55px]", render: (job) => (
    <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{job.job_number ? `#${job.job_number}` : "—"}</span>
  )},
  { key: "date", label: "Date", w: "w-[85px]", sortable: true, render: (job) => (
    <span className="text-xs">{formatDateDisplay(job.pickup_datetime)}</span>
  )},
  { key: "time", label: "Time", w: "w-[60px]", render: (job) => (
    <span className="font-medium text-xs" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{formatTime(job.pickup_datetime)}</span>
  )},
  { key: "driver", label: "Dr#", w: "w-[45px]", render: (job) => (
    <span className="text-xs text-center block font-semibold text-cyan-700 dark:text-cyan-300" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{job.driver_number || "—"}</span>
  )},
  { key: "class", label: "Class", w: "w-[55px]", render: (job) => (
    <span className="text-xs">{formatDisplayClass(job.vehicle_class || job.vehicle_type)}</span>
  )},
  { key: "status", label: "Status", w: "w-[50px]", render: (job) => (
    <span className="text-center block">
      {job.status === "no_show" ? (
        <span className="text-[10px] font-bold text-red-500 tracking-wider">NS</span>
      ) : (
        <span className={`inline-block w-3 h-3 rounded-[2px] ${job.status === "cancelled" ? "bg-red-500/80" : "bg-emerald-400/80"}`} title={job.status} />
      )}
    </span>
  )},
  { key: "account", label: "Acct", w: "w-[55px]", render: (job) => (
    <span className="text-xs text-muted-foreground">{job.account_number || "—"}</span>
  )},
  { key: "client", label: "Client", render: (job) => (
    <span className="text-xs font-medium truncate block">{job.client_name}</span>
  )},
  { key: "pickup", label: "Pickup", render: (job) => (
    <span className="text-xs max-w-[180px] truncate block" title={job.pickup_location}>{job.pickup_location}</span>
  )},
  { key: "dropoff", label: "Dropoff", render: (job) => (
    <span className="text-xs max-w-[180px] truncate block" title={job.dropoff_location}>{job.dropoff_location}</span>
  )},
  { key: "net", label: "Net", w: "w-[70px]", adminOnly: true, render: (job) => {
    const n = computeHouseNet(job);
    if (n === null) return <span className="text-xs text-muted-foreground/40 text-right block tabular-nums">—</span>;
    return (
      <span
        className={`text-xs text-right block tabular-nums font-semibold ${n >= 0 ? "text-emerald-500 dark:text-emerald-400" : "text-red-500"}`}
        style={{ fontFamily: "'IBM Plex Sans', monospace" }}
        title={`Total ${fmtMoney(job.total)} − Driver ${fmtMoney(job.driver_total)} − remittances = ${fmtMoney(n)}`}
      >
        {fmtMoney(n)}
      </span>
    );
  }},
];

const HISTORY_DEFAULT_ORDER = ["job_number", "date", "time", "driver", "class", "status", "account", "client", "pickup", "dropoff", "net"];

function getStoredHistoryColOrder() {
  try {
    const stored = localStorage.getItem(HISTORY_COL_STORAGE_KEY);
    if (stored) {
      const order = JSON.parse(stored);
      const validKeys = HISTORY_COLUMNS.map((c) => c.key);
      const filtered = order.filter((k) => validKeys.includes(k));
      const missing = validKeys.filter((k) => !filtered.includes(k));
      return [...filtered, ...missing];
    }
  } catch (e) { console.warn("Parse stored history column order:", e); }
  return HISTORY_DEFAULT_ORDER;
}

// ========== MAIN HISTORY PAGE ==========
export default function HistoryPage() {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortNewest, setSortNewest] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [page, setPage] = useState(1);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [dateFrom, setDateFrom] = useState(get30DaysAgoKey);
  const [dateTo, setDateTo] = useState(getYesterdayKey);
  const tableRef = useRef(null);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Column reorder state
  const [colOrder, setColOrder] = useState(getStoredHistoryColOrder);
  const [dragCol, setDragCol] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const orderedCols = useMemo(
    () => colOrder
      .map((k) => HISTORY_COLUMNS.find((c) => c.key === k))
      .filter(Boolean)
      .filter((c) => !c.adminOnly || isAdmin),
    [colOrder, isAdmin]
  );

  const handleDragStart = (e, key) => { setDragCol(key); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", key); };
  const handleDragOver = (e, key) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (key !== dragCol) setDragOver(key); };
  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetKey) { setDragCol(null); setDragOver(null); return; }
    const newOrder = [...colOrder];
    const fromIdx = newOrder.indexOf(dragCol);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragCol);
    setColOrder(newOrder);
    localStorage.setItem(HISTORY_COL_STORAGE_KEY, JSON.stringify(newOrder));
    setDragCol(null); setDragOver(null);
  };
  const handleDragEnd = () => { setDragCol(null); setDragOver(null); };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [j, d] = await Promise.all([api.get("/jobs"), api.get("/drivers")]);
      setJobs(j.data);
      setDrivers(d.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const getDriverName = useCallback((id) => drivers.find((d) => d.id === id)?.name || "—", [drivers]);

  const todayKey = useMemo(() => getTodayDateKey(), []);

  const filtered = useMemo(() => {
    return jobs
      .filter((j) => {
        // Show all past jobs regardless of status — unassigned/assigned past jobs
        // are dispatch-failure records, kept truthful (not lied about).
        // Exclude today
        if (j.pickup_datetime && j.pickup_datetime.startsWith(todayKey)) return false;
        // Date range
        const jobDate = j.pickup_datetime ? j.pickup_datetime.split("T")[0] : "";
        if (dateFrom && jobDate < dateFrom) return false;
        if (dateTo && jobDate > dateTo) return false;
        // Status filter
        if (statusFilter !== "all" && j.status !== statusFilter) return false;
        // Search
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const driverName = j.driver_id ? getDriverName(j.driver_id).toLowerCase() : "";
          const vehicleClass = (j.vehicle_class || j.vehicle_type || "").toLowerCase();
          const time = j.pickup_datetime ? formatTime(j.pickup_datetime) : "";
          const date = j.pickup_datetime ? formatDateDisplay(j.pickup_datetime) : "";
          const acct = (j.account_number || "").toLowerCase();
          return (
            j.client_name?.toLowerCase().includes(q) ||
            j.pickup_location?.toLowerCase().includes(q) ||
            j.dropoff_location?.toLowerCase().includes(q) ||
            driverName.includes(q) ||
            vehicleClass.includes(q) ||
            time.includes(q) ||
            date.includes(q) ||
            acct.includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const dateA = a.pickup_datetime || "";
        const dateB = b.pickup_datetime || "";
        return sortNewest ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
      });
  }, [jobs, statusFilter, searchQuery, sortNewest, todayKey, dateFrom, dateTo, getDriverName]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginatedJobs = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, page]);

  // Reset page on filter changes
  useEffect(() => { setPage(1); setFocusedIdx(-1); }, [statusFilter, searchQuery, sortNewest, dateFrom, dateTo]);

  const toggleStatusFilter = (status) => {
    setStatusFilter((prev) => (prev === status ? "all" : status));
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (selectedJob) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx((prev) => Math.min(prev + 1, paginatedJobs.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && focusedIdx >= 0 && focusedIdx < paginatedJobs.length) {
        e.preventDefault();
        setSelectedJob(paginatedJobs[focusedIdx]);
      } else if (e.key === "Escape") {
        setFocusedIdx(-1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [paginatedJobs, focusedIdx, selectedJob]);

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIdx >= 0 && tableRef.current) {
      const rows = tableRef.current.querySelectorAll("tbody tr");
      if (rows[focusedIdx]) {
        rows[focusedIdx].scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [focusedIdx]);

  return (
    <div data-testid="history-page" className="space-y-4">
      <div>
        <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>History</h1>
        <p className="text-sm text-muted-foreground mt-1">All past jobs — yesterday and older</p>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 max-w-xs min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-card border-border"
            data-testid="history-search"
          />
        </div>

        {/* Result count */}
        <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="history-result-count">
          {filtered.length} {filtered.length === 1 ? "job" : "jobs"}
        </span>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-card border-border w-[130px] h-8 text-xs"
            data-testid="history-date-from"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-card border-border w-[130px] h-8 text-xs"
            data-testid="history-date-to"
          />
        </div>

        <div className="flex-1" />

        {/* Status toggles */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => toggleStatusFilter("completed")}
            className="inline-flex items-center gap-1.5 transition-all"
            data-testid="history-filter-completed"
          >
            <span className={`w-4 h-4 rounded-[3px] border transition-all ${
              statusFilter === "completed"
                ? "bg-emerald-400/80 border-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.4)]"
                : "bg-emerald-400/30 border-emerald-500/30"
            }`} />
            <span className="text-xs text-muted-foreground">Completed</span>
          </button>
          <button
            onClick={() => toggleStatusFilter("no_show")}
            className="inline-flex items-center gap-1.5 transition-all"
            data-testid="history-filter-no-show"
          >
            <span className={`text-[10px] font-bold tracking-wider transition-all px-1 ${
              statusFilter === "no_show"
                ? "text-red-500"
                : "text-red-500/40"
            }`}>NS</span>
            <span className="text-xs text-muted-foreground">No-Show</span>
          </button>
          <button
            onClick={() => toggleStatusFilter("cancelled")}
            className="inline-flex items-center gap-1.5 transition-all"
            data-testid="history-filter-cancelled"
          >
            <span className={`w-4 h-4 rounded-[3px] border transition-all ${
              statusFilter === "cancelled"
                ? "bg-red-500/80 border-red-400 shadow-[0_0_6px_rgba(239,68,68,0.4)]"
                : "bg-red-500/30 border-red-500/30"
            }`} />
            <span className="text-xs text-muted-foreground">Cancelled</span>
          </button>
          {filtered.length > 0 && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
              const esc = (v) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
              const rows = [["Time","Date","Client","Pickup","Dropoff","Class","Driver","Account","Status"]];
              filtered.forEach((j) => {
                rows.push([
                  formatTime(j.pickup_datetime),
                  formatPickupDate(j.pickup_datetime),
                  j.client_name || `${j.first_name || ""} ${j.last_name || ""}`.trim() || "",
                  j.pickup_location || "",
                  j.dropoff_location || "",
                  j.vehicle_class || j.vehicle_type || "",
                  j.driver_id ? getDriverName(j.driver_id) : "",
                  j.account_number || "",
                  j.status || "",
                ]);
              });
              const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `history_${dateFrom}_${dateTo}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }} data-testid="history-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
            </Button>
          )}
        </div>
      </div>

      {/* Table with sticky header */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <div className="max-h-[calc(100vh-280px)] overflow-y-auto min-w-[800px]" ref={tableRef}>
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow className="bg-muted/20 hover:bg-muted/20">
                {orderedCols.map((col) => (
                  <TableHead
                    key={col.key}
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragOver={(e) => handleDragOver(e, col.key)}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={handleDragEnd}
                    className={`cursor-grab active:cursor-grabbing select-none transition-colors ${col.w || ""} ${
                      dragCol === col.key ? "opacity-40" : ""
                    } ${dragOver === col.key ? "bg-primary/10 border-l-2 border-primary" : ""} ${
                      col.sortable ? "hover:text-foreground" : ""
                    }`}
                    onClick={col.sortable ? () => setSortNewest((p) => !p) : undefined}
                    data-testid={`history-col-header-${col.key}`}
                  >
                    {col.sortable ? (
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortNewest ? <ArrowDown className="w-3 h-3 text-primary" /> : <ArrowUp className="w-3 h-3 text-primary" />}
                      </span>
                    ) : col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={orderedCols.length} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : paginatedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={orderedCols.length} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2" data-testid="history-empty-state">
                      <CalendarDays className="w-8 h-8 text-muted-foreground/30" />
                      <span className="text-sm text-muted-foreground">All clear — no past jobs match your filters</span>
                      <span className="text-xs text-muted-foreground/50">Try adjusting your date range or search</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedJobs.map((job, idx) => (
                  <RowHoverPreview key={job.id} job={job}>
                    <TableRow
                      className={`cursor-pointer transition-colors ${
                        focusedIdx === idx
                          ? "bg-primary/10 border-l-2 border-l-primary"
                          : idx % 2 === 1 ? "bg-muted/5 hover:bg-muted/20" : "hover:bg-muted/20"
                      }`}
                      onClick={() => setSelectedJob(job)}
                      data-testid={`history-row-${job.id}`}
                    >
                      {orderedCols.map((col) => (
                        <TableCell key={col.key} className={col.w || ""}>
                          {col.render(job)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </RowHoverPreview>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {filtered.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between px-1" data-testid="history-pagination">
          <span className="text-xs text-muted-foreground">
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setPage((p) => Math.max(1, p - 1)); setFocusedIdx(-1); }}
              disabled={page <= 1}
              data-testid="history-page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setFocusedIdx(-1); }}
              disabled={page >= totalPages}
              data-testid="history-page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* View-only dialog */}
      <JobViewDialog
        job={selectedJob}
        open={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        getDriverName={getDriverName}
      />
    </div>
  );
}
